/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from 'react';
import PageSkeleton from '../../components/common/page-skeleton';
import ScreenHeader from '../../components/common/screen-header';
import { Message } from 'primereact/message';
import { Button } from 'sharedUi/Button';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { logMasterService } from '../../services/log-master.service';
import type { LogMaster } from '../../types';
import { extractErrorMessage } from '../../utils/error-handler';
import DeleteConfirmDialog from '../../components/dialogs/delete-confirm-dialog';
import { AuditInfoCard } from '../../components/common/audit-info-card';
import { Field, Section, Pill, type PillTone } from '../../components/common/detail-view';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { useCanUpdate, useCanDelete } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';

/** Log-level colour: red for errors, amber for warnings, calm blue otherwise. */
const levelTone = (lvl?: string): PillTone =>
  lvl === 'ERROR' ? 'red' : lvl === 'WARNING' ? 'amber' : 'blue';

const LogMasterView = () => {
  const { id, isReady, navigateToEdit, navigateToList } = useSecureNavigation({
    entity: 'log-master',
    basePath: '/log-master',
  });
  const canUpdate = useCanUpdate(JOB_NAMES.LOG_MASTER);
  const canDelete = useCanDelete(JOB_NAMES.LOG_MASTER);

  const [data, setData] = useState<LogMaster | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useBreadcrumbTitle(data?.logCode);

  useEffect(() => {
    if (!isReady || !id) return;
    (async () => {
      try {
        setLoading(true);
        const res = await logMasterService.getById(id);
        setData(res);
      } catch (err: any) {
        setError(extractErrorMessage(err, 'Failed to fetch log master'));
      } finally {
        setLoading(false);
      }
    })();
  }, [isReady, id]);

  const handleEdit = () => { if (id) navigateToEdit(id); };
  const handleDeleteClick = () => setDeleteDialogOpen(true);
  const handleDeleteConfirm = async () => {
    if (!id) return;
    try {
      await logMasterService.delete(id);
      setDeleteDialogOpen(false);
      navigateToList();
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to delete log master'));
      setDeleteDialogOpen(false);
    }
  };
  const handleDeleteCancel = () => setDeleteDialogOpen(false);

  if (loading) {
    return (
      <PageSkeleton />
    );
  }

  if (error || !data) {
    return (
      <div className="p-3">
        <Message severity="error" text={error || 'Log Master not found'} className="mb-4 w-full" />
        <Button label="Back to Log Master" icon="pi pi-arrow-left" severity="secondary" outlined onClick={() => navigateToList()} />
      </div>
    );
  }

  return (
    <PermissionGuard jobName={JOB_NAMES.LOG_MASTER}>
      <div className="p-3" data-testid="SCR-LogMaster-View">
        {/* Header */}
        <ScreenHeader
          icon="pi pi-file-edit"
          title="Log Master Details"
          onBack={() => navigateToList()}
        />

        {/* Log Master Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-2">
          <div className="flex items-start gap-2.5">
            <div className="w-12 h-12 rounded bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
              <i className="pi pi-file-edit text-xl text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                <h2 className="text-base font-semibold text-ink font-mono">{data.logCode}</h2>
                {data.isDelete ? (
                  <Pill tone="red">Deleted</Pill>
                ) : (
                  <Pill tone="green">Active</Pill>
                )}
              </div>
              <p className="text-xs text-ink-muted">
                {[data.logtype, data.category].filter(Boolean).join(' · ')}
                {(data.logtype || data.category) && ' · '}
                Retention: {data.retentionPeriod} days
              </p>
            </div>
          </div>

          {!data.isDelete && (
            <div className="flex gap-1.5 sm:self-center">
              {canUpdate && <Button label="Edit" icon="pi pi-pencil" onClick={handleEdit} size="small" data-testid="LogMasterView.Button.Edit" />}
              {canDelete && <Button label="Delete" icon="pi pi-trash" severity="danger" onClick={handleDeleteClick} size="small" data-testid="LogMasterView.Button.Delete" />}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex flex-col gap-2">
          <Section title="Basic Information">
            <Field label="Event Code" value={data.logCode} mono wide />
            <Field label="Description" value={data.description} wide />
          </Section>

          <Section title="Classification">
            <Field label="Layer" value={<Pill tone="blue">{data.layer || '-'}</Pill>} />
            <Field label="Log Level" value={<Pill tone={levelTone(data.logLevel)}>{data.logLevel || 'INFO'}</Pill>} />
            <Field label="Log Type" value={<Pill tone="neutral">{data.logtype || '-'}</Pill>} />
            <Field label="Category" value={<Pill tone="neutral">{data.category || '-'}</Pill>} />
          </Section>

          <Section title="Retention & Flags">
            <Field label="Retention Period" value={`${data.retentionPeriod} days`} />
            <Field label="Usage Trackable" value={<Pill tone={data.isUsageTrackable ? 'blue' : 'neutral'}>{data.isUsageTrackable ? 'Yes' : 'No'}</Pill>} />
            <Field label="Sensitive" value={<Pill tone={data.isSensitive ? 'red' : 'neutral'}>{data.isSensitive ? 'Yes' : 'No'}</Pill>} />
            <Field label="Can Be Logged" value={<Pill tone={data.canBeLogged ? 'green' : 'neutral'}>{data.canBeLogged ? 'Yes' : 'No'}</Pill>} />
          </Section>

          <Section title="Message Template" cols={1}>
            <div className="bg-gray-50 dark:bg-gray-700/50 p-2.5 rounded border border-gray-200 dark:border-gray-600">
              <code className="text-ink font-mono text-xs break-words">
                {data.messageTemplate || '-'}
              </code>
            </div>

            {/* Parameters Table */}
            {data.parameters && data.parameters.length > 0 && (
              <div>
                <label className="text-xs text-ink-muted block mb-1">Parameters</label>
                <div className="border border-gray-200 dark:border-gray-600 rounded overflow-hidden">
                  {/* Header */}
                  <div className="grid grid-cols-12 gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                    <div className="col-span-3">Name</div>
                    <div className="col-span-3">Label</div>
                    <div className="col-span-2">Datatype</div>
                    <div className="col-span-2 text-center">Key Field</div>
                    <div className="col-span-2 text-center">Sensitive</div>
                  </div>
                  {/* Rows */}
                  {data.parameters.map((param, idx) => (
                    <div
                      key={`${param.name}-${idx}`}
                      className="grid grid-cols-12 gap-2 px-3 py-1.5 border-t border-gray-200 dark:border-gray-600 items-center"
                    >
                      <div className="col-span-3">
                        <span className="font-mono text-xs text-ink">{param.name}</span>
                      </div>
                      <div className="col-span-3">
                        <span className="text-xs text-gray-700 dark:text-gray-300">{param.label}</span>
                      </div>
                      <div className="col-span-2">
                        <Pill tone="neutral">{param.datatype}</Pill>
                      </div>
                      <div className="col-span-2 flex justify-center">
                        {param.isKeyField ? (
                          <i className="pi pi-check-circle text-blue-500" style={{ fontSize: '0.9rem' }} />
                        ) : (
                          <i className="pi pi-minus-circle text-gray-300 dark:text-gray-600" style={{ fontSize: '0.9rem' }} />
                        )}
                      </div>
                      <div className="col-span-2 flex justify-center">
                        {param.isSensitive ? (
                          <i className="pi pi-shield text-red-500" style={{ fontSize: '0.9rem' }} />
                        ) : (
                          <i className="pi pi-minus-circle text-gray-300 dark:text-gray-600" style={{ fontSize: '0.9rem' }} />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Section>

          {data.templateParameters && Object.keys(data.templateParameters).length > 0 && (
            <Section title="Template Parameters" cols={1}>
              <Field label="Template Parameters" value={data.templateParameters} json />
            </Section>
          )}

          <AuditInfoCard
            createdAt={(data as any).createdAt}
            updatedAt={(data as any).updatedAt}
            createdBy={(data as any).createdBy}
            updatedBy={(data as any).updatedBy}
          />
        </div>

        <DeleteConfirmDialog
          visible={deleteDialogOpen}
          onCancel={handleDeleteCancel}
          onDelete={handleDeleteConfirm}
          message="Are you sure you want to delete this log master? This action will mark it as deleted."
          data-testid="LogMasterView.Dialog.Delete"
        />
      </div>
    </PermissionGuard>
  );
};

export default LogMasterView;
